<template>
  <div class="mx-10 ma-5">
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-card flat class="pa-5">
      <div>
        <PageTitleWithSearch
          :title="'Expense Types'"
          :btnName="'Add New Expense Type'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/expense-types/create'"
          :btnPermission="'expense_types.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        />

        <template v-if="expenseTypes.length > 0">
          <DataTableWithPagination
            :headers="headers"
            :items="expenseTypes"
            :loading="loading"
            :page="page"
            :total="total"
            @pageChange="page = $event"
          >
            <template #row="{ item, index }">
              <tr
                :class="[
                  { 'first-row': index === 0 },
                  index % 2 === 0 ? 'dark-row' : 'light-row',
                  'custom-row',
                ]"
              >
                <td>{{ item.name }}</td>
                <td>{{ item.description }}</td>
                <td>{{ item.gl_account_id }}</td>
                <td class="text-left">
                  <v-btn
                    v-if="hasPermission('expense_types.edit')"
                    class="ma-1"
                    color="#FFE0B2"
                    :to="'/expense-types/edit/' + item.id"
                    fab
                    x-small
                    style="box-shadow: none;"
                  >
                    <v-icon small color="#FF9800">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    class="ma-1"
                    disabled
                    fab
                    x-small
                    style="box-shadow: none;"
                    ><v-icon small color="#FF9800">mdi-pencil</v-icon></v-btn
                  >
                  <v-btn
                    v-if="hasPermission('expense_types.delete')"
                    class="my-1"
                    x-small
                    color="#FFCDD2"
                    @click.stop="dropMemberConfirm(item.id)"
                    fab
                    style="box-shadow: none;"
                  >
                    <v-icon dark small color="#F44336">mdi-delete</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    class="my-1"
                    disabled
                    fab
                    x-small
                    style="box-shadow: none;"
                  >
                    <v-icon small color="#FF9800">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </DataTableWithPagination>
        </template>
        <template v-else>
          <v-alert type="info" class="mt-5" outlined>
            No expense types found for the applied filters.
          </v-alert>
        </template>
      </div>
    </v-card>
    <ConfirmModal
      :text="'Do you want to drop this Expense Type?'"
      :title="'Drop Expense Type'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'success'"
      @closeSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { ExpenseService } from "@/services/expense-service.js";
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import Snackbars from "@/components/shared/Snackbars";
import ConfirmModal from "@/components/shared/ConfirmModal";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import Alert from "@/components/shared/Alert.vue";
import { checkError } from "@/config/errors.js";

export default {
  components: {
    PageTitleWithSearch,
    Snackbars,
    ConfirmModal,
    DataTableWithPagination,
    Alert,
  },
  data() {
    return {
      dropId: null,
      confirmOpen: false,
      loading: true,
      expenseTypes: [],
      errors: null,
      snackbar: false,
      successMsg: "",
      search: "",
      page: 1,
      total: 0,
      headers: [
        { text: "Name", value: "name" },
        { text: "Description", value: "description" },
        { text: "GL Account", value: "gl_account_id" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  created() {
    this.fetchExpenseTypes();
  },
  watch: {
    search() {
      this.page = 1;
      this.fetchExpenseTypes();
    },
    page() {
      this.fetchExpenseTypes();
    },
  },
  methods: {
    async fetchExpenseTypes() {
      this.loading = true;
      try {
        const response = await ExpenseService.getExpenseTypes(
          this.search,
          this.page
        );
        this.expenseTypes = response.data?.data || [];
        this.total = response.data?.meta?.last_page || 0;
      } catch (error) {
        this.expenseTypes = [];
        this.total = 0;
        console.error("Error fetching expense types:", error);
      } finally {
        this.loading = false;
      }
    },

    async deleteExpenseType(id) {
      try {
        await ExpenseService.deleteExpenseType(id);
        this.successMsg = "Expense Type deleted successfully.";
        this.snackbar = true;
        this.fetchExpenseTypes();
      } catch (error) {
        let data = error.response?.data?.error || "Unknown error";

        let errorOutput = checkError.errorFormatting(data);
        let errorsArray = [];
        if (Array.isArray(errorOutput)) {
          errorsArray = errorOutput.filter((e) => !!e && e.trim());
          if (errorsArray.length === 0) errorsArray = [data];
        } else if (errorOutput && errorOutput.trim()) {
          errorsArray = [errorOutput];
        } else {
          errorsArray = [data];
        }
        this.errors = errorsArray;
      }
    },

    dropMemberConfirm(id) {
      this.dropId = id;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.deleteExpenseType(this.dropId);
    },

    closeSnackbar() {
      this.snackbar = false;
    },
    searchChange(value) {
      this.search = value;
    },
    onDismissed() {
      this.errors = null;
    },
  },
};
</script>
